/**
 * OverflowToolbar — three-tier responsive toolbar.
 *
 * As the toolbar narrows, items collapse: full label → icon+tooltip → into
 * an overflow ("More") menu. Driven by a ResizeObserver on the toolbar's
 * own width, not the viewport.
 *
 * Pattern from Salesforce Lightning Utility Bar, Office Ribbon, Steam.
 *
 * Use when: a toolbar has more than ~3 actions and lives inside a window
 * that resizes. For static toolbars use `AppToolbar` directly.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import { useLocale } from '../i18n/useLocale';

export interface ToolbarAction {
  key: string;
  label: string;
  icon: ReactElement;
  /** Action priority — lower numbers stay full-label longer. */
  priority?: number;
  /** "primary" highlights, otherwise outline. */
  intent?: 'default' | 'primary';
  disabled?: boolean;
  onClick?: () => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'nowrap',
    overflow: 'hidden',
    minWidth: 0,
  },
  measureRow: {
    /* Off-screen measurement row — used to compute natural widths */
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    top: '-9999px',
    left: '-9999px',
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
});

interface OverflowToolbarProps {
  actions: ToolbarAction[];
  /** Width in pixels below which all items become icon-only. Default 320. */
  iconOnlyBelow?: number;
}

/**
 * Layout strategy:
 *   stage 1 — full labels, all visible
 *   stage 2 — when too wide: convert lower-priority items to icon-only with tooltip
 *   stage 3 — when still too wide: pop overflow items into a "More" menu
 */
export function OverflowToolbar({ actions, iconOnlyBelow = 320 }: OverflowToolbarProps) {
  const styles = useStyles();
  const { t } = useLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(Number.MAX_SAFE_INTEGER);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const sorted = [...actions].sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));
  /* Heuristic: ~140px per labelled button, ~36px per icon-only. */
  const fullCost = sorted.length * 140;
  const iconCost = sorted.length * 36;
  const useIconOnly = containerWidth < fullCost && containerWidth >= iconCost;
  const useOverflow = containerWidth < iconCost || containerWidth < iconOnlyBelow;

  /* Always keep the highest-priority items visible; spill the rest. */
  const visibleCount = useOverflow
    ? Math.max(1, Math.floor((containerWidth - 48) / 36))
    : sorted.length;
  const visible = sorted.slice(0, visibleCount);
  const overflow = sorted.slice(visibleCount);

  return (
    <div ref={containerRef} className={styles.root} role="toolbar">
      {visible.map((a) => {
        const button = (
          <Button
            key={a.key}
            appearance={a.intent === 'primary' ? 'primary' : 'outline'}
            size="small"
            icon={a.icon}
            disabled={a.disabled}
            onClick={a.onClick}
            aria-label={useIconOnly || useOverflow ? a.label : undefined}
          >
            {!useIconOnly && !useOverflow ? a.label : null}
          </Button>
        );
        return (useIconOnly || useOverflow) ? (
          <Tooltip key={a.key} content={a.label} relationship="description">
            {button}
          </Tooltip>
        ) : button;
      })}
      {overflow.length > 0 ? (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="outline"
              size="small"
              icon={<MoreHorizontalRegular />}
              aria-label={t('common.aria.moreActions', { count: overflow.length })}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {overflow.map((a) => (
                <MenuItem
                  key={a.key}
                  icon={a.icon}
                  disabled={a.disabled}
                  onClick={a.onClick}
                >
                  {a.label}
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      ) : null}
    </div>
  );
}
